import { axios, API } from "@/api";
import { track as mpTrack } from "@/lib/mixpanel";
import { track as phTrack } from "@/lib/posthog";

/**
 * Lightweight event tracker. Fire-and-forget — never blocks UI.
 * Every call sends to POST /api/activity/log which writes to user_activity collection.
 * Also mirrors events to Mixpanel when available.
 */
export function trackEvent(eventType, metadata = {}) {
  axios.post(`${API}/activity/log`, {
    event_type: eventType,
    metadata,
  }).catch(() => {});
  mpTrack(eventType, metadata);
  phTrack(eventType, metadata);
}

/**
 * Track a page view. Call once in useEffect on mount.
 */
export function trackPageView(pageName, metadata = {}) {
  trackEvent("page_view", { page: pageName, ...metadata });
}

/**
 * Track an external link click (website, app store, etc.)
 */
export function trackLinkClick(linkType, url, metadata = {}) {
  trackEvent("link_click", { link_type: linkType, url, ...metadata });
}

/**
 * Track a sort change
 */
export function trackSort(sortBy, metadata = {}) {
  trackEvent("sort_change", { sort_by: sortBy, ...metadata });
}

/**
 * Track a tab switch
 */
export function trackTabSwitch(tabName, metadata = {}) {
  trackEvent("tab_switch", { tab: tabName, ...metadata });
}

/**
 * Track an error. Call from catch blocks.
 * @param {string} context - Where the error happened (e.g., "offer_detail_fetch", "login")
 * @param {Error|string} error - The error object or message
 */
export function trackError(context, error, metadata = {}) {
  const message = typeof error === "string" ? error : (error?.response?.data?.detail || error?.message || "Unknown error");
  const status = error?.response?.status || null;
  trackEvent("error", { context, message, status, ...metadata });
}

/**
 * Track scroll depth on a page. Fire once per page session with max depth.
 */
export function trackScrollDepth(pageName, maxDepthPercent) {
  trackEvent("scroll_depth", { page: pageName, max_depth: maxDepthPercent });
}

/**
 * Track how many deal cards the user actually saw before leaving.
 * Fires once on page unmount with the set of viewed business IDs.
 */
export function trackDealsViewed(pageName, viewedIds) {
  trackEvent("deals_viewed", { page: pageName, count: viewedIds.length, business_ids: viewedIds });
}
